import { computeSessionTrainerHonorarEur } from "./trainer-session-honorar.js";
import { normalizeTrainerId } from "./trainer-assistant-honorar.js";
import { authEmailsEquivalent } from "./normalize-email-for-auth-match.js";

export const COORDINATOR_FEE_TYPES = Object.freeze({
  NONE: "none",
  PERCENT: "percent",
  PER_HOUR: "per_hour",
  FLAT_SESSION: "flat_session",
});

export const SETTLEMENT_PAYOUT_ROLE_COORDINATOR = "coordinator";

function nz(v) {
  return String(v ?? "").trim();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * @param {unknown} type
 * @returns {string}
 */
export function normalizeCoordinatorFeeType(type) {
  const t = nz(type).toLowerCase();
  if (t === COORDINATOR_FEE_TYPES.PERCENT) return COORDINATOR_FEE_TYPES.PERCENT;
  if (t === COORDINATOR_FEE_TYPES.PER_HOUR)
    return COORDINATOR_FEE_TYPES.PER_HOUR;
  if (t === COORDINATOR_FEE_TYPES.FLAT_SESSION)
    return COORDINATOR_FEE_TYPES.FLAT_SESSION;
  return COORDINATOR_FEE_TYPES.NONE;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function parseCoordinatorFeeValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return round2(n);
}

/**
 * @param {string} feeType
 * @param {unknown} feeValue
 * @param {Record<string, unknown>|null|undefined} session
 * @returns {number}
 */
export function computeCoordinatorFeeEurFromConfig(feeType, feeValue, session) {
  const type = normalizeCoordinatorFeeType(feeType);
  if (type === COORDINATOR_FEE_TYPES.NONE) return 0;
  const value = parseCoordinatorFeeValue(feeValue);
  if (value === null || value <= 0) return 0;
  const gross = computeSessionTrainerHonorarEur(session);
  if (gross <= 0) return 0;

  if (type === COORDINATOR_FEE_TYPES.PERCENT) {
    const pct = Math.min(100, value);
    return round2((gross * pct) / 100);
  }
  if (type === COORDINATOR_FEE_TYPES.PER_HOUR) {
    const mins = Number(session?.duration_min) || 0;
    if (mins <= 0) return 0;
    return round2((mins / 60) * value);
  }
  if (type === COORDINATOR_FEE_TYPES.FLAT_SESSION) {
    return value;
  }
  return 0;
}

/**
 * @param {Record<string, unknown>|null|undefined} session
 * @returns {number}
 */
export function getSessionCoordinatorFeeEur(session) {
  const cid = normalizeTrainerId(session?.coordinator_trainer_id);
  if (!cid) return 0;
  const stored = parseCoordinatorFeeValue(session?.coordinator_fee_eur);
  if (stored !== null && stored > 0)
    return Math.min(stored, computeSessionTrainerHonorarEur(session));
  const type = normalizeCoordinatorFeeType(session?.coordinator_fee_type);
  if (type === COORDINATOR_FEE_TYPES.NONE) return 0;
  return computeCoordinatorFeeEurFromConfig(
    type,
    session?.coordinator_fee_value,
    session,
  );
}

/**
 * @param {number|null|undefined} grossEur
 * @param {unknown} assistantShareEur
 * @param {unknown} coordinatorShareEur
 */
export function validateHonorarSplitShares(
  grossEur,
  assistantShareEur,
  coordinatorShareEur,
) {
  const gross = Number(grossEur);
  const assistant = parseCoordinatorFeeValue(assistantShareEur) ?? 0;
  const coordinator = parseCoordinatorFeeValue(coordinatorShareEur) ?? 0;
  if (assistant <= 0 && coordinator <= 0)
    return { ok: true, assistant: 0, coordinator: 0 };
  if (!Number.isFinite(gross) || gross <= 0) {
    return { ok: false, code: "gross-missing" };
  }
  if (assistant + coordinator > gross + 1e-9) {
    return { ok: false, code: "shares-exceed-gross" };
  }
  return { ok: true, assistant, coordinator };
}

/**
 * @param {{
 *   feeType?: string,
 *   feeValue?: unknown,
 *   coordinatorTrainerId?: string,
 *   grossHonorarEur?: number,
 *   assistantShareEur?: unknown,
 *   sessionForCalc?: Record<string, unknown>,
 * }} input
 */
export function buildSessionCoordinatorFeePersistFields(input) {
  const safe = input && typeof input === "object" ? input : {};
  const cid = normalizeTrainerId(safe.coordinatorTrainerId);
  const type = normalizeCoordinatorFeeType(safe.feeType);
  const session =
    safe.sessionForCalc && typeof safe.sessionForCalc === "object"
      ? safe.sessionForCalc
      : {};
  const gross = Number(
    safe.grossHonorarEur ?? computeSessionTrainerHonorarEur(session),
  );

  if (!cid || type === COORDINATOR_FEE_TYPES.NONE) {
    return {
      coordinator_fee_type: COORDINATOR_FEE_TYPES.NONE,
      coordinator_fee_value: null,
      coordinator_fee_eur: null,
      coordinator_trainer_id: null,
    };
  }

  const value = parseCoordinatorFeeValue(safe.feeValue);
  if (value === null || value <= 0) {
    return {
      coordinator_fee_type: COORDINATOR_FEE_TYPES.NONE,
      coordinator_fee_value: null,
      coordinator_fee_eur: null,
      coordinator_trainer_id: null,
    };
  }

  const feeEur = computeCoordinatorFeeEurFromConfig(type, value, session);
  if (feeEur <= 0) {
    return {
      coordinator_fee_type: COORDINATOR_FEE_TYPES.NONE,
      coordinator_fee_value: null,
      coordinator_fee_eur: null,
      coordinator_trainer_id: null,
    };
  }

  const assistant = parseCoordinatorFeeValue(safe.assistantShareEur) ?? 0;
  const split = validateHonorarSplitShares(gross, assistant, feeEur);
  if (!split.ok) {
    return { ok: false, code: split.code };
  }

  return {
    ok: true,
    coordinator_fee_type: type,
    coordinator_fee_value: value,
    coordinator_fee_eur: Math.min(feeEur, gross),
    coordinator_trainer_id: cid,
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 */
export function getTrainerDefaultCoordinatorFeeFields(trainer) {
  return {
    feeType: normalizeCoordinatorFeeType(trainer?.default_coordinator_fee_type),
    feeValue: parseCoordinatorFeeValue(trainer?.default_coordinator_fee_value),
  };
}

/**
 * Standard-Koordinator-Anteil beim Planen (Trainer-Stammdaten).
 *
 * @param {Record<string, unknown>|null|undefined} trainer
 */
export function resolvePlanningCoordinatorDefaultsFromTrainer(trainer) {
  const def = getTrainerDefaultCoordinatorFeeFields(trainer);
  if (def.feeType === COORDINATOR_FEE_TYPES.NONE || def.feeValue === null) {
    return { feeType: COORDINATOR_FEE_TYPES.NONE, feeValue: "" };
  }
  return { feeType: def.feeType, feeValue: String(def.feeValue) };
}

/**
 * Fixer Koordinator-Anteil in den Trainer-Stammdaten (default_coordinator_fee_*).
 * @param {Record<string, unknown>|null|undefined} trainer
 */
export function hasTrainerFixedCoordinatorFeeDefault(trainer) {
  const def = getTrainerDefaultCoordinatorFeeFields(trainer);
  return (
    def.feeType !== COORDINATOR_FEE_TYPES.NONE &&
    def.feeValue !== null &&
    def.feeValue > 0
  );
}

/**
 * Manuelles Koordinator-Anteil-Feld in der Terminplanung nur, wenn weder Haupt- noch Assistenten-Trainer
 * einen fixen Standard-Koordinator-Anteil in den Stammdaten haben.
 *
 * @param {{
 *   mainTrainer?: Record<string, unknown>|null,
 *   assistantTrainer?: Record<string, unknown>|null,
 * }} [ctx]
 */
export function shouldShowPlanningSessionCoordinatorFeeFields(ctx = {}) {
  if (hasTrainerFixedCoordinatorFeeDefault(ctx.mainTrainer)) return false;
  if (hasTrainerFixedCoordinatorFeeDefault(ctx.assistantTrainer)) return false;
  return true;
}

/**
 * @param {string} editFeeType
 * @param {unknown} editFeeValue
 * @param {Record<string, unknown>|null|undefined} trainerForDefaults
 */
export function resolveCoordinatorFeeSource(
  editFeeType,
  editFeeValue,
  trainerForDefaults,
) {
  const type = normalizeCoordinatorFeeType(editFeeType);
  if (type === COORDINATOR_FEE_TYPES.NONE) return "manual_override";
  const def = getTrainerDefaultCoordinatorFeeFields(trainerForDefaults);
  if (type !== def.feeType) return "manual_override";
  const editVal = parseCoordinatorFeeValue(editFeeValue);
  if (editVal === null || def.feeValue === null) return "manual_override";
  if (Math.abs(editVal - def.feeValue) > 1e-9) return "manual_override";
  return "trainer_default";
}

/**
 * Persistenzfelder für Session-Planung (nur Koordinator-Sicht).
 *
 * @param {{
 *   enabled?: boolean,
 *   feeType?: string,
 *   feeValue?: unknown,
 *   trainers?: object[],
 *   coordinatorAuth?: { email?: string, userId?: string },
 *   coordinatorTrainerId?: string,
 *   grossHonorarEur?: number,
 *   assistantShareEur?: unknown,
 *   sessionForCalc?: Record<string, unknown>,
 *   trainerForDefaults?: Record<string, unknown>|null,
 * }} input
 */
export function buildPlanningSessionCoordinatorFeePersistPayload(input) {
  const safe = input && typeof input === "object" ? input : {};
  if (!safe.enabled) {
    return { ok: true, fields: {} };
  }

  const trainers = Array.isArray(safe.trainers) ? safe.trainers : [];
  const explicitId = normalizeTrainerId(safe.coordinatorTrainerId);
  const recipient = explicitId
    ? trainers.find((tr) => normalizeTrainerId(tr?.id) === explicitId) || {
        id: explicitId,
      }
    : findCoordinatorFeeRecipientTrainer(trainers, safe.coordinatorAuth || {});
  const type = normalizeCoordinatorFeeType(safe.feeType);
  const wantsFee =
    type !== COORDINATOR_FEE_TYPES.NONE &&
    parseCoordinatorFeeValue(safe.feeValue) !== null;

  if (wantsFee && !recipient?.id) {
    return { ok: false, code: "coordinator-recipient-missing" };
  }

  const built = buildSessionCoordinatorFeePersistFields({
    feeType: safe.feeType,
    feeValue: safe.feeValue,
    coordinatorTrainerId: recipient?.id,
    grossHonorarEur: safe.grossHonorarEur,
    assistantShareEur: safe.assistantShareEur,
    sessionForCalc: safe.sessionForCalc,
  });

  if (built.ok === false) {
    return { ok: false, code: built.code };
  }

  const coordinator_fee_source = resolveCoordinatorFeeSource(
    safe.feeType,
    safe.feeValue,
    safe.trainerForDefaults,
  );
  const { ok: _ok, ...feeFields } = built;

  return {
    ok: true,
    fields: {
      ...feeFields,
      coordinator_fee_source,
      coordinator_fee_snapshot_at: new Date().toISOString(),
    },
  };
}

/**
 * Wie {@link buildPlanningSessionCoordinatorFeePersistPayload}, legt bei Bedarf automatisch
 * einen Trainer-Datensatz für den Koordinator (login_email) an.
 *
 * @param {Parameters<typeof buildPlanningSessionCoordinatorFeePersistPayload>[0] & {
 *   ensureRecipient?: boolean,
 *   coordinatorProfile?: { firstName?: string, lastName?: string },
 * }} input
 */
export async function buildPlanningSessionCoordinatorFeePersistPayloadAsync(
  input,
) {
  const safe = input && typeof input === "object" ? input : {};
  if (!safe.enabled) {
    return { ok: true, fields: {} };
  }

  let trainers = Array.isArray(safe.trainers) ? safe.trainers : [];
  let recipient = findCoordinatorFeeRecipientTrainer(
    trainers,
    safe.coordinatorAuth || {},
  );
  const type = normalizeCoordinatorFeeType(safe.feeType);
  const wantsFee =
    type !== COORDINATOR_FEE_TYPES.NONE &&
    parseCoordinatorFeeValue(safe.feeValue) !== null;

  if (wantsFee && !recipient?.id && safe.ensureRecipient !== false) {
    const { ensureCoordinatorFeeRecipientTrainerRecord } =
      await import("./trainers.js");
    const tenantId = trainers.map((tr) => nz(tr?.tenant_id)).find(Boolean);
    const ensured = await ensureCoordinatorFeeRecipientTrainerRecord({
      ...(safe.coordinatorAuth || {}),
      firstName: safe.coordinatorProfile?.firstName,
      lastName: safe.coordinatorProfile?.lastName,
      tenantId,
    });
    if (ensured.ok && ensured.data?.id) {
      recipient = ensured.data;
      if (!trainers.some((tr) => nz(tr?.id) === nz(ensured.data.id))) {
        trainers = [...trainers, ensured.data];
      }
    } else if (ensured.code) {
      return { ok: false, code: ensured.code, error: ensured.error };
    }
  }

  return buildPlanningSessionCoordinatorFeePersistPayload({
    ...safe,
    trainers,
    coordinatorTrainerId: recipient?.id,
  });
}

/**
 * Trainer-Datensatz des Koordinators für Honorar-Auszahlung (login_email).
 *
 * @param {object[]} trainers
 * @param {{ email?: string, userId?: string }} ctx
 */
export function findCoordinatorFeeRecipientTrainer(trainers, ctx = {}) {
  const email = nz(ctx.email).toLowerCase();
  const userId = nz(ctx.userId);
  const list = Array.isArray(trainers) ? trainers : [];
  if (email) {
    const byEmail = list.find((tr) =>
      authEmailsEquivalent(tr?.login_email, email),
    );
    if (byEmail?.id) return byEmail;
  }
  if (userId) {
    const owned = list.filter((tr) => nz(tr?.created_by) === userId);
    const byOwnerWithLogin = owned.find((tr) => nz(tr?.login_email));
    if (byOwnerWithLogin?.id) return byOwnerWithLogin;
    if (owned[0]?.id) return owned[0];
  }
  return null;
}

/**
 * @param {string} trainerId
 * @param {Record<string, unknown>} session
 */
export function isCoordinatorSettlementSessionForTrainer(trainerId, session) {
  const tid = normalizeTrainerId(trainerId);
  if (!tid) return false;
  if (!session?.attendance_confirmed) return false;
  if (String(session.coordinator_settlement_id || "").trim()) return false;
  if (normalizeTrainerId(session.coordinator_trainer_id) !== tid) return false;
  return getSessionCoordinatorFeeEur(session) > 0;
}
